import { Loader, Button } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { parsePatientChartSplat } from '../patientChartPath'
import { useMedplum } from '@medplum/react'
import { DocumentReference } from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { formatDateTime } from '@/utils'
import { PdfViewer } from '@/components/PdfViewer'
import { IconDownload } from '@tabler/icons-react'
import { Consent } from '@medplum/fhirtypes'
import { MouseEvent } from 'react'
import { textToPdf } from '@/utils/textToPdf.ts'
import { useScrollContext } from '@/context/ScrollContext'

type Props = {
  title: string
  sourceResourceId?: string
  customCssForCDANavigation?: string
}

type Attachment = {
  title?: string
  url?: string
  data?: string
  contentType?: string
}

export const ConsentDocuments = ({ title, sourceResourceId }: Props) => {
  const params = useParams()
  const medplum = useMedplum()
  const [resourceId, setResourceId] = useState<string | undefined>(sourceResourceId)
  const [resource, setResource] = useState<DocumentReference>()
  const [isNotFound, setIsNotFound] = useState(false)
  const [attachment, setAttachment] = useState<Attachment>()
  const [consentData, setConsentData] = useState<Consent | undefined>()
  const { scrollToTop } = useScrollContext()

  useEffect(() => {
    if (sourceResourceId) {
      setResourceId(sourceResourceId)
      return
    }
    const parsedResourceId = parsePatientChartSplat(params['*']).resourceId

    if (parsedResourceId && parsedResourceId !== resourceId) {
      setResourceId(parsedResourceId)
    }
  }, [params, resourceId, sourceResourceId])

  useEffect(() => {
    scrollToTop()
  }, [resourceId, scrollToTop])
  useEffect(() => {
    if (resourceId) {
      medplum
        .search('DocumentReference', `_id=${resourceId}&_revinclude=Provenance:target`)
        .then(async (resources) => {
          const neededResource = resources.entry?.find(
            (resource) => resource.resource?.id === resourceId,
          )?.resource as DocumentReference | undefined
          if (neededResource && neededResource.author && neededResource.custodian) {
            const consent = await medplum.searchOne(
              'Consent',
              `patient=${neededResource.author[0].reference}&organization=${neededResource.custodian.reference}&source-reference=${neededResource.resourceType}/${neededResource.id}`,
            )
            if (!consent) {
              throw new Error('Consent not found')
            } else {
              setConsentData(consent)
            }
          }

          if (neededResource) {
            setResource(neededResource)
            setIsNotFound(false)
          }
        })
        .catch((e) => {
          setIsNotFound(true)
          logError('patient', 'Failed to fetch consent document', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resource',
            color: 'red',
          })
        })
    }

    return () => {
      setResource(undefined)
      setIsNotFound(false)
    }
  }, [medplum, resourceId])

  useEffect(() => {
    if (resource) {
      const attachment = resource.content[0]?.attachment
      setAttachment(attachment)
    }
  }, [resource])

  function getSource(doc?: DocumentReference) {
    const sourceAgent = doc?.custodian?.display || '-'
    return sourceAgent
  }

  function getType(docRef: DocumentReference) {
    return docRef.type?.coding?.[0]?.display || docRef.type?.text || '-'
  }

  function getConsentStatus(consentData?: Consent) {
    return consentData?.status || '-'
  }
  const handleDownloadConsentDoc = async (event: MouseEvent<HTMLButtonElement>) => {
    if (attachment?.data) {
      void textToPdf(
        attachment?.data || '',
        `${attachment?.title || 'consent-document'}`.toLowerCase().replace(/\s/g, '-'),
      )
    } else if (attachment?.contentType === 'application/pdf' && attachment?.url) {
      event.preventDefault()
      const res = await fetch(attachment?.url)
      const blob = await res.blob()
      const tempUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = tempUrl
      a.download = `${attachment?.title} Consent Document.pdf`
      a.click()
      URL.revokeObjectURL(tempUrl)
    }
  }
  if (!resourceId || isNotFound) {
    return null
  }

  if (!resource) {
    return (
      <div
        style={{ width: 'calc(100% - 560px - var(--zoom-panel-width, 0px))' }}
        className="fixed left-[560px] flex h-full items-center justify-center gap-3 text-sm text-[#7D7987]"
      >
        <Loader size="sm" />
        Loading document...
      </div>
    )
  }

  return (
    <div className="relative box-border p-2 whitespace-pre-wrap">
      <div className="w-full bg-white pt-2">
        <p className="mb-4 text-2xl font-bold">{title}</p>
        <div className="flex justify-start gap-28">
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-[#7D7987]">Source:</p>
              <p className="text-sm font-bold text-[#7D7987]">Type:</p>
              <p className="text-sm font-bold text-[#7D7987]">Document Name:</p>
              <p className="text-sm font-bold text-[#7D7987]">Consent Status:</p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-base text-sm">{getSource(resource)}</p>
              <p className="text-base text-sm">{getType(resource)}</p>
              <p className="text-base text-sm">{attachment?.title || resource.type?.text || '-'}</p>
              <p className="text-base text-sm">{getConsentStatus(consentData)}</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-[#7D7987]">Added on:</p>
              <p className="text-sm font-bold text-[#7D7987]">Modified on:</p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-base text-sm">
                {resource.date ? formatDateTime(resource.date, '-', true) : '-'}
              </p>
              <p className="text-base text-sm">
                {resource.meta?.lastUpdated
                  ? formatDateTime(resource.meta.lastUpdated, '-', true)
                  : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <Button
        className="height-[24px] mt-4 mb-4 inline-block cursor-pointer"
        size="xs"
        onClick={(e) => handleDownloadConsentDoc(e)}
      >
        Download
        <IconDownload size={14} className="ml-1" />
      </Button>
      <div>
        <p className="mb-4 text-xl">Attachments</p>
        {attachment?.url && <PdfViewer fileUrl={attachment?.url} />}
      </div>
    </div>
  )
}
